import {observer} from 'mobx-react';
import React, {useEffect, useRef} from 'react';

import {Tracker} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {ExperimentImpressionEvent, useFeatures} from '@udemy/react-experimentation-provider';
import {
    ShoppingClient,
    ShoppingListName,
    ShoppingItem as UdemyShoppingItem,
    CouponStore,
} from '@udemy/shopping';
import {AuthenticatedUser, useUDData} from '@udemy/ud-data';

import {getConfig} from 'src/features/cart/config';
import {CART_UPSELL_FEATURE, CartUpsellFeatureVariation} from 'src/lib/experimentation-helper';

import styles from '../../cart.module.less';
import {CertificationUpsellSection} from '../certification-upsell';
import {RedeemCouponWithCartCouponStore} from '../redeem-coupon/redeem-coupon-with-cart-coupon-store.react-component';
import {ShoppingListTrackActionsProps} from '../shopping-list/shopping-list.react-component';
import {ShoppingListWrapper} from './shopping-list-wrapper.react-component';

const listIsHidden = (listName: ShoppingListName) =>
    ShoppingClient.lists[listName].isEmpty && ['saved_for_later', 'wishlist'].includes(listName);

const hasItemsOfType = (items: UdemyShoppingItem[], buyableType: string) =>
    items.some((item) => item.buyable.buyable_object_type === buyableType);

interface ShoppingListPaneProps {
    showSaveForLaterAction?: boolean;
    showCodingExercisesBadge?: boolean;
    trackAction?: ({action, item, destListName}: ShoppingListTrackActionsProps) => void;
    shoppingCartVariantMobile?: boolean;
    couponStore?: CouponStore;
    isEmptyCart?: boolean;
}

export const ShoppingListsPane = observer(
    ({
        showSaveForLaterAction = true,
        showCodingExercisesBadge = false,
        trackAction,
        shoppingCartVariantMobile,
        couponStore,
        isEmptyCart,
    }: ShoppingListPaneProps) => {
        const {me: udMe} = useUDData();
        const {gettext, interpolate, ngettext} = useI18n();
        const config = getConfig(gettext, interpolate, ngettext);
        const isXlMin = useMatchMedia('xl-min');
        const allLists = (udMe as AuthenticatedUser).id
            ? config.shoppingList.all
            : config.shoppingList.all.filter((list) => list !== 'wishlist');

        const {featureVariantAssignments} = useFeatures([CART_UPSELL_FEATURE]);
        const upsellAssignment = featureVariantAssignments.get(CART_UPSELL_FEATURE);
        const {variation: cartUpsellFeatureVariation}: {variation: CartUpsellFeatureVariation} =
            upsellAssignment?.configuration ?? CartUpsellFeatureVariation.Control;

        const hasFiredImpressionRef = useRef(false);
        const isEligible = !ShoppingClient.lists.cart.isEmpty && !!upsellAssignment;
        useEffect(() => {
            if (isEligible && !hasFiredImpressionRef.current) {
                hasFiredImpressionRef.current = true;
                Tracker.publishEvent(
                    new ExperimentImpressionEvent(
                        upsellAssignment.experimentIds,
                        upsellAssignment.isLocalDefault,
                    ),
                );
            }
        }, [isEligible, upsellAssignment]);

        const renderCartSublists = () => {
            const cartItems = ShoppingClient.lists.cart.items;

            return config.shoppingList.cartSublists
                .filter((sublistType) => hasItemsOfType(cartItems, sublistType))
                .map((sublistType) => (
                    <ShoppingListWrapper
                        key={`cart-${sublistType}`}
                        listName="cart"
                        sublistType={sublistType}
                        showSaveForLaterAction={showSaveForLaterAction}
                        trackAction={trackAction}
                        actionsLayout={isXlMin ? 'vertical' : 'horizontal'}
                        showCodingExercisesBadge={showCodingExercisesBadge}
                    />
                ));
        };

        const visibleLists = allLists.map((listName) => {
            if (listIsHidden(listName as ShoppingListName)) {
                return null;
            }

            if (listName === 'cart' && !ShoppingClient.lists.cart.isEmpty) {
                return (
                    <React.Fragment key="cart">
                        {renderCartSublists()}
                        {cartUpsellFeatureVariation !== CartUpsellFeatureVariation.Control && (
                            <CertificationUpsellSection
                                listName="upsell_module"
                                trackAction={trackAction}
                                actionsLayout={isXlMin ? 'vertical' : 'horizontal'}
                                featureVariation={cartUpsellFeatureVariation}
                            />
                        )}
                    </React.Fragment>
                );
            }

            return (
                <ShoppingListWrapper
                    key={listName as ShoppingListName}
                    listName={listName as ShoppingListName}
                    showSaveForLaterAction={showSaveForLaterAction}
                    trackAction={trackAction}
                    actionsLayout={isXlMin ? 'vertical' : 'horizontal'}
                    showCodingExercisesBadge={showCodingExercisesBadge}
                />
            );
        });

        return (
            <>
                {visibleLists[0]}
                {!isEmptyCart && couponStore && shoppingCartVariantMobile && (
                    <div className={styles['cart-redeem-coupon-section-mobile']}>
                        <RedeemCouponWithCartCouponStore
                            couponStore={couponStore}
                            isInCheckoutPane={true}
                        />
                    </div>
                )}
                {visibleLists.slice(1)}
            </>
        );
    },
);
